import { useState, useEffect, useRef, useMemo } from 'react'

// Placeholder SVG as data URL
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect width="400" height="300" fill="%23e2e8f0"/%3E%3Cg fill="%2394a3b8"%3E%3Cpath d="M200 120c-16.569 0-30 13.431-30 30s13.431 30 30 30 30-13.431 30-30-13.431-30-30-30zm0 50c-11.028 0-20-8.972-20-20s8.972-20 20-20 20 8.972 20 20-8.972 20-20 20z"/%3E%3Cpath d="M280 90H120c-11.028 0-20 8.972-20 20v80c0 11.028 8.972 20 20 20h160c11.028 0 20-8.972 20-20v-80c0-11.028-8.972-20-20-20zm10 100c0 5.514-4.486 10-10 10H120c-5.514 0-10-4.486-10-10v-80c0-5.514 4.486-10 10-10h160c5.514 0 10 4.486 10 10v80z"/%3E%3C/g%3E%3C/svg%3E'

export default function ImageCarousel({ images, alt, className = '' }) {
    // Filter out empty/invalid images first
    const validImages = Array.isArray(images)
        ? images.filter(img => img && typeof img === 'string' && img.trim())
        : []

    const [imageError, setImageError] = useState(false)

    // Generate random initial index and delay offset to prevent synchronous switching
    const randomization = useMemo(() => {
        if (validImages.length <= 1) return { initialIndex: 0, delayOffset: 0 }

        return {
            initialIndex: Math.floor(Math.random() * validImages.length),
            // Random delay offset between 0-9 seconds to stagger carousel switches
            delayOffset: Math.floor(Math.random() * 9000)
        }
    }, [validImages.length])

    const [currentIndex, setCurrentIndex] = useState(randomization.initialIndex)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [shouldAutoScroll, setShouldAutoScroll] = useState(false)
    const [scrollPosition, setScrollPosition] = useState(50) // 0-100, default center
    const intervalRef = useRef(null)
    const imageRef = useRef(null)
    const containerRef = useRef(null)
    const AUTO_ADVANCE_DELAY = 10000 // 10 seconds

    // Determine which image to show
    const displayImage = validImages.length === 0 ? PLACEHOLDER_IMAGE : (imageError ? PLACEHOLDER_IMAGE : validImages[currentIndex])
    const hasMultipleImages = validImages.length > 1

    // Handle image load error
    const handleImageError = () => {
        setImageError(true)
    }

    // Check if image is taller than container and needs auto-scroll
    function checkImageHeight() {
        if (!imageRef.current || !containerRef.current) return

        const imageHeight = imageRef.current.naturalHeight
        const containerHeight = containerRef.current.clientHeight

        // If image is significantly taller than container, enable auto-scroll
        setShouldAutoScroll(imageHeight > containerHeight * 1.2)
    }

    // Handle mouse movement to scroll image
    function handleMouseMove(e) {
        if (!shouldAutoScroll || !containerRef.current) return

        const rect = containerRef.current.getBoundingClientRect()
        const mouseY = e.clientY - rect.top
        const containerHeight = rect.height
        const centerY = containerHeight / 2

        // Calculate distance from center (-1 to 1)
        // Negative = mouse above center (scroll up), Positive = mouse below center (scroll down)
        const distanceFromCenter = (mouseY - centerY) / centerY

        // Convert to scroll position (0 = top, 50 = center, 100 = bottom)
        // When mouse is at center, show center (50)
        // When mouse is at top, show top (0)
        // When mouse is at bottom, show bottom (100)
        const newPosition = 50 + (distanceFromCenter * 50)

        // Clamp between 0 and 100
        setScrollPosition(Math.max(0, Math.min(100, newPosition)))
    }

    // Reset to center when mouse leaves
    function handleMouseLeave() {
        setScrollPosition(50)
    }

    // Reset error state when images change
    useEffect(() => {
        setImageError(false)
    }, [validImages.length])

    // Check image height when current image changes
    useEffect(() => {
        // Reset scroll position to center when image changes
        setScrollPosition(50)
        // Small delay to ensure image is loaded
        const timer = setTimeout(checkImageHeight, 100)
        return () => clearTimeout(timer)
    }, [currentIndex])

    // Auto-advance effect with randomized delay (only for multiple images)
    useEffect(() => {
        // Skip auto-advance for single images or errors
        if (validImages.length <= 1 || imageError) {
            return
        }

        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }

        // Set up initial delay with randomization, then regular intervals
        const initialTimeout = setTimeout(() => {
            // Set up recurring interval
            intervalRef.current = setInterval(() => {
                setIsTransitioning(true)
                setTimeout(() => {
                    setCurrentIndex(prevIndex => (prevIndex + 1) % validImages.length)
                    setIsTransitioning(false)
                }, 300) // Duration of fade transition
            }, AUTO_ADVANCE_DELAY)

            // Trigger first transition
            setIsTransitioning(true)
            setTimeout(() => {
                setCurrentIndex(prevIndex => (prevIndex + 1) % validImages.length)
                setIsTransitioning(false)
            }, 300)
        }, randomization.delayOffset)

        // Cleanup on unmount
        return () => {
            clearTimeout(initialTimeout)
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
            }
        }
    }, [validImages.length, randomization.delayOffset, imageError])

    // If no images or only one image (or error), show it without carousel UI
    if (validImages.length <= 1 || imageError) {
        return (
            <div
                ref={containerRef}
                className="relative overflow-hidden"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                <img
                    ref={imageRef}
                    src={validImages.length === 0 ? PLACEHOLDER_IMAGE : (imageError ? PLACEHOLDER_IMAGE : validImages[0])}
                    alt={alt}
                    className={className}
                    style={shouldAutoScroll ? {
                        objectFit: 'cover',
                        objectPosition: `center ${scrollPosition}%`,
                        transition: 'object-position 0.3s ease-out'
                    } : {}}
                    loading="lazy"
                    onLoad={checkImageHeight}
                    onError={handleImageError}
                />
            </div>
        )
    }

    const handleDotClick = (index) => {
        setIsTransitioning(true)
        setTimeout(() => {
            setCurrentIndex(index)
            setIsTransitioning(false)
        }, 300)

        // Reset the auto-advance timer when user manually changes image
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
        }
        intervalRef.current = setInterval(() => {
            setIsTransitioning(true)
            setTimeout(() => {
                setCurrentIndex(prevIndex => (prevIndex + 1) % validImages.length)
                setIsTransitioning(false)
            }, 300)
        }, AUTO_ADVANCE_DELAY)
    }

    const handlePrevious = (event) => {
        event?.stopPropagation?.()
        setIsTransitioning(true)
        setTimeout(() => {
            const newIndex = currentIndex === 0 ? validImages.length - 1 : currentIndex - 1
            setCurrentIndex(newIndex)
            setIsTransitioning(false)
        }, 300)
    }

    const handleNext = (event) => {
        event?.stopPropagation?.()
        setIsTransitioning(true)
        setTimeout(() => {
            const newIndex = (currentIndex + 1) % validImages.length
            setCurrentIndex(newIndex)
            setIsTransitioning(false)
        }, 300)
    }

    return (
        <div
            ref={containerRef}
            className="group relative overflow-hidden"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <img
                ref={imageRef}
                src={displayImage}
                alt={alt}
                className={`transition-all duration-300 hover:scale-110 ${isTransitioning ? 'opacity-0' : 'opacity-100'} ${className}`}
                style={shouldAutoScroll ? {
                    objectFit: 'cover',
                    objectPosition: `center ${scrollPosition}%`,
                    transition: 'object-position 0.3s ease-out'
                } : {}}
                loading="lazy"
                onLoad={checkImageHeight}
                onError={handleImageError}
            />

            {/* Navigation arrows - only visible on hover */}
            {validImages.length > 1 && (
                <>
                    <button
                        type="button"
                        onClick={handlePrevious}
                        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 text-slate-700 opacity-0 shadow-lg transition hover:bg-white group-hover:opacity-100"
                        aria-label="Previous image"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        onClick={handleNext}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-1.5 text-slate-700 opacity-0 shadow-lg transition hover:bg-white group-hover:opacity-100"
                        aria-label="Next image"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                        </svg>
                    </button>
                </>
            )}

            {/* Indicator dots */}
            {validImages.length > 1 && (
                <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
                    {validImages.map((_, index) => (
                        <button
                            key={index}
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation()
                                handleDotClick(index)
                            }}
                            className={`h-2 w-2 rounded-full transition ${
                                index === currentIndex
                                    ? 'bg-white shadow-md'
                                    : 'bg-white/60 hover:bg-white/80'
                            }`}
                            aria-label={`Go to image ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
